import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Form, Input, Typography, Upload, UploadFile } from 'antd';
import { StyledIssueDescription, StyledIssueDescriptionFormItem } from './IssueDescription.styles';
import { useUploadProps } from './IssueDescription.upload';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { IS_DESKTOP } from 'app/modules/service/utils/constants';
import { useServiceRequestContext } from 'app/modules/service/context/ServiceContext';

const normalizeUploadFileList = (value: unknown): UploadFile[] => (Array.isArray(value) ? value : []);

export const IssueDescription = (): JSX.Element => {
  const { handleAddServiceCaseNext, handleAddServiceCaseBack, handleIssueDescription, issueDescriptionData } =
    useServiceRequestContext();

  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isMaxCount, setIsMaxCount] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>(() =>
    normalizeUploadFileList(issueDescriptionData?.issuePhotos)
  );

  const issueDescriptionValue = Form.useWatch('issueDescription', form);

  const hasFileErrors = fileList.some(file => file.status === 'error' || file.status === 'uploading');

  const isSubmitDisabled = !issueDescriptionValue || hasFileErrors;

  const { uploadPropsWithList, uploadPropsNoList } = useUploadProps({
    fileList,
    setFileList,
    setIsMaxCount,
    form,
  });

  const onValuesChange = (
    _: { issueDescription: string; issuePhotos: UploadFile[] },
    allFields: { issueDescription: string; issuePhotos: UploadFile[] }
  ): void => {
    handleIssueDescription({ ...allFields });
  };

  const onNext = (): void => {
    form.submit();
  };

  const onFinish = (): void => {
    handleAddServiceCaseNext();
  };

  return (
    <StyledIssueDescription vertical align="start" justify="start" gap={kccThemeConstants.SPACING.LG}>
      <Typography.Title level={3} style={{ margin: 0, fontWeight: 700 }}>
        {t('service.issueDescription.title')}
      </Typography.Title>

      <Form
        form={form}
        autoComplete="off"
        layout="vertical"
        requiredMark="optional"
        style={{ width: '100%', margin: 0 }}
        initialValues={{
          issueDescription: issueDescriptionData?.issueDescription,
          issuePhotos: normalizeUploadFileList(issueDescriptionData?.issuePhotos),
        }}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
      >
        <StyledIssueDescriptionFormItem
          name="issueDescription"
          label={t('service.issueDescription.form.label.issueDescription')}
          required
        >
          <Input.TextArea
            showCount
            rows={4}
            maxLength={500}
            allowClear={false}
            placeholder={t('service.issueDescription.form.placeholder.issueDescription')}
            style={{ width: '100%', maxWidth: '755px' }}
          />
        </StyledIssueDescriptionFormItem>

        <StyledIssueDescriptionFormItem
          name="issuePhotos"
          label={t('service.issueDescription.form.label.issuePhotos')}
          valuePropName="fileList"
          getValueFromEvent={(event): UploadFile[] => normalizeUploadFileList(event?.fileList)}
          style={{ marginBottom: 0 }}
        >
          <Upload.Dragger
            {...uploadPropsWithList}
            style={{ display: isMaxCount || fileList.length === 3 ? 'none' : 'block' }}
          >
            <SvgIcon name="download" className="download-icon" />
            <p className="ant-upload-text">{t('service.issueDescription.upload.text')}</p>
            <p className="ant-upload-hint">{t('service.issueDescription.upload.hint')}</p>
          </Upload.Dragger>

          {!IS_DESKTOP && (
            <div style={{ display: isMaxCount || fileList.length === 3 ? 'none' : 'block' }}>
              <Flex
                justify="center"
                align="center"
                style={{
                  margin: `${kccThemeConstants.SPACING.SM} 0px`,
                  maxWidth: '755px',
                }}
              >
                {t('service.issueDescription.upload.or')}
              </Flex>

              <Upload {...uploadPropsNoList} capture="environment">
                <KccButton
                  type="secondary"
                  icon={<SvgIcon name="camera" className="camera-icon" />}
                  style={{ width: '100%', maxWidth: '755px', borderColor: 'black' }}
                >
                  {t('service.issueDescription.upload.takePhoto')}
                </KccButton>
              </Upload>
            </div>
          )}
        </StyledIssueDescriptionFormItem>
      </Form>

      <Flex
        justify="space-between"
        align="center"
        style={{ width: '100%', maxWidth: kccThemeConstants.SCREEN.CONTENT_MAX_WIDTH }}
      >
        <KccButton
          type="link"
          onClick={handleAddServiceCaseBack}
          icon={<SvgIcon name="leftArrow" className="back-icon" />}
          iconPosition="start"
          className="issue-description__back-btn"
        >
          {t('common.back')}
        </KccButton>
        <KccButton
          disabled={isSubmitDisabled}
          icon={<SvgIcon name="rightArrow" className="next-icon" />}
          iconPosition="end"
          className="issue-description__next-btn"
          onClick={onNext}
          type="secondary"
        >
          {t('common.next')}
        </KccButton>
      </Flex>
    </StyledIssueDescription>
  );
};
